import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Plus, Send, Clock, CheckCircle, AlertTriangle, User } from "lucide-react";
import { Badge } from "../../components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function ManagementIssuesAdmin() {
  const { toast } = useToast();
  const [issues, setIssues] = useState([]);
  const [opsManagers, setOpsManagers] = useState([]);
  const [kitchens, setKitchens] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOpsManager, setSelectedOpsManager] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  const [form, setForm] = useState({
    ops_manager_id: "",
    outlet_id: "",
    kitchen_id: "",
    title: "",
    description: "",
    resolution_deadline: "",
    priority: "medium"
  });

  useEffect(() => {
    fetchData();
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { credentials: "include" });
      if (res.ok) setCurrentUser(await res.json());
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [issuesRes, opsRes, kitchensRes] = await Promise.all([
        fetch(`${API_URL}/api/ops-extended/management-issues`, { credentials: "include" }),
        fetch(`${API_URL}/api/admin/ops-managers`, { credentials: "include" }),
        fetch(`${API_URL}/api/ops-extended/kitchens`, { credentials: "include" })
      ]);
      
      if (issuesRes.ok) setIssues(await issuesRes.json());
      if (opsRes.ok) setOpsManagers(await opsRes.json());
      if (kitchensRes.ok) setKitchens(await kitchensRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOutlets = async (kitchenId) => {
    try {
      const res = await fetch(`${API_URL}/api/ops-extended/outlets?kitchen_id=${kitchenId}`, { credentials: "include" });
      if (res.ok) setOutlets(await res.json());
    } catch (error) {
      console.error("Error fetching outlets:", error);
    }
  };

  const handleCreateIssue = async () => {
    if (!form.ops_manager_id || !form.title || !form.description || !form.resolution_deadline) {
      toast({ title: "Error", description: "Please fill all required fields", variant: "destructive" });
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/ops-extended/management-issues?admin_id=${currentUser?.user_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form)
      });
      
      if (res.ok) {
        toast({ title: "Issue created successfully" });
        setDialogOpen(false);
        setForm({
          ops_manager_id: "",
          outlet_id: "",
          kitchen_id: "",
          title: "",
          description: "",
          resolution_deadline: "",
          priority: "medium"
        });
        fetchData();
      } else {
        const error = await res.json();
        toast({ title: "Error", description: error.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to create issue", variant: "destructive" });
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "bg-gray-500",
      medium: "bg-yellow-500",
      high: "bg-orange-500",
      urgent: "bg-red-500"
    };
    return colors[priority] || "bg-gray-500";
  };

  const getStatusBadge = (status) => {
    if (status === "resolved") {
      return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Resolved</Badge>;
    }
    return <Badge className="bg-blue-500"><Clock className="h-3 w-3 mr-1" />Open</Badge>;
  };

  const getOpsManagerName = (opsId) => {
    const ops = opsManagers.find(o => o.user_id === opsId);
    return ops ? ops.name : "Unknown";
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  const openIssues = issues.filter(i => i.status === "open");
  const resolvedIssues = issues.filter(i => i.status === "resolved");

  return (
    <div className="space-y-6" data-testid="management-issues-admin">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Open Issues</p>
                <p className="text-2xl font-bold">{openIssues.length}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Resolved Issues</p>
                <p className="text-2xl font-bold">{resolvedIssues.length}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Resolution Time</p>
                <p className="text-2xl font-bold">
                  {resolvedIssues.length > 0 
                    ? Math.round(resolvedIssues.reduce((acc, i) => acc + (i.resolution_time_minutes || 0), 0) / resolvedIssues.length)
                    : 0} min
                </p>
              </div>
              <Clock className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Issue */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Management Issues
          </CardTitle>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" /> Create Issue
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Issue for Ops Manager</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Ops Manager *</Label>
                  <Select value={form.ops_manager_id} onValueChange={(v) => setForm({ ...form, ops_manager_id: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select ops manager" />
                    </SelectTrigger>
                    <SelectContent>
                      {opsManagers.map((ops) => (
                        <SelectItem key={ops.user_id} value={ops.user_id}>{ops.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Kitchen (Optional)</Label>
                  <Select value={form.kitchen_id} onValueChange={(v) => { setForm({ ...form, kitchen_id: v }); fetchOutlets(v); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select kitchen" />
                    </SelectTrigger>
                    <SelectContent>
                      {kitchens.map((k) => (
                        <SelectItem key={k.kitchen_id} value={k.kitchen_id}>{k.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {form.kitchen_id && outlets.length > 0 && (
                  <div>
                    <Label>Outlet (Optional)</Label>
                    <Select value={form.outlet_id} onValueChange={(v) => setForm({ ...form, outlet_id: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select outlet" />
                      </SelectTrigger>
                      <SelectContent>
                        {outlets.map((o) => (
                          <SelectItem key={o.outlet_id} value={o.outlet_id}>{o.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Title *</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Issue title" />
                </div>
                <div>
                  <Label>Description *</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the issue..." rows={4} />
                </div>
                <div>
                  <Label>Resolution Deadline *</Label>
                  <Input type="datetime-local" value={form.resolution_deadline} onChange={(e) => setForm({ ...form, resolution_deadline: e.target.value })} />
                </div>
                <Button onClick={handleCreateIssue} className="w-full">Send Issue</Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ops Manager</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Deadline</TableHead>
                <TableHead>Resolution Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {issues.map((issue) => (
                <TableRow key={issue.issue_id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {getOpsManagerName(issue.ops_manager_id)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{issue.title}</p>
                      <p className="text-sm text-muted-foreground truncate max-w-xs">{issue.description}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getPriorityColor(issue.priority)}>{issue.priority}</Badge>
                  </TableCell>
                  <TableCell>{getStatusBadge(issue.status)}</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {new Date(issue.resolution_deadline).toLocaleDateString()}
                      <br />
                      <span className="text-muted-foreground">
                        {new Date(issue.resolution_deadline).toLocaleTimeString()}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {issue.resolution_time_minutes 
                      ? `${Math.round(issue.resolution_time_minutes)} min`
                      : "-"}
                  </TableCell>
                </TableRow>
              ))}
              {issues.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    No issues created yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Ops Manager Performance */}
      <Card>
        <CardHeader>
          <CardTitle>Ops Manager Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ops Manager</TableHead>
                <TableHead>Total Issues</TableHead>
                <TableHead>Resolved</TableHead>
                <TableHead>Open</TableHead>
                <TableHead>Avg Resolution Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {opsManagers.map((ops) => {
                const opsIssues = issues.filter(i => i.ops_manager_id === ops.user_id);
                const resolved = opsIssues.filter(i => i.status === "resolved");
                const open = opsIssues.filter(i => i.status === "open");
                const avgTime = resolved.length > 0 
                  ? Math.round(resolved.reduce((acc, i) => acc + (i.resolution_time_minutes || 0), 0) / resolved.length)
                  : 0;
                
                return (
                  <TableRow key={ops.user_id}>
                    <TableCell className="font-medium">{ops.name}</TableCell>
                    <TableCell>{opsIssues.length}</TableCell>
                    <TableCell className="text-green-600">{resolved.length}</TableCell>
                    <TableCell className="text-yellow-600">{open.length}</TableCell>
                    <TableCell>{avgTime > 0 ? `${avgTime} min` : "-"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
